import React, { useState, useMemo } from 'react';
import {
    Box,
    Typography,
    IconButton,
    Select,
    MenuItem,
    FormControl,
    InputLabel,
    useMediaQuery,
    Button,
} from '@mui/material';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import backgroundImage from 'assets/images/coworking.jpg';
import dayjs from 'dayjs';

const SearchBanner = ({
    startDate,
    endDate,
    handlePrevWeek,
    handleNextWeek,
    selectedArea,
    setSelectedArea,
    selectedService,
    setSelectedService,
    isLoggedIn,
    // New props for day selector
    coworkingSpaces = [],
    selectedDayIndex,
    onDaySelect,
    // Sort props
    sortBy,
    setSortBy,
    // Price display mode
    priceDisplayMode,
    setPriceDisplayMode,
}) => {
    const areas = [
        { value: 'shoreditch', label: 'Greater London', disabled: false },
        { value: 'central', label: 'Central London', disabled: true },
        { value: 'west', label: 'West End', disabled: true },
        { value: 'east', label: 'East London', disabled: true }
    ];

    const services = [
        { value: 'HD', label: 'Hot Desk', disabled: false },
        { value: 'MR', label: 'Meeting Room', disabled: false },
        { value: 'ST', label: 'Private Office', disabled: true }
    ];

    const sortOptions = [
        { value: 'price', label: 'Price (Low to High)' },
        { value: 'distance', label: 'Distance (Nearest)' },
    ];

    const isMobile = useMediaQuery((theme) => theme.breakpoints.down('sm'));
    const [showFilters, setShowFilters] = useState(!isMobile);

    // Generate days from startDate to endDate
    const days = useMemo(() => {
        const result = [];
        let current = dayjs(startDate);
        const end = dayjs(endDate);
        const today = dayjs().startOf('day');
        
        while (current.isBefore(end) || current.isSame(end, 'day')) {
            result.push({
                date: current,
                dayName: current.format('ddd'),
                dayDate: current.format('D'),
                monthName: current.format('MMM'),
                isPast: current.isBefore(today, 'day'),
                isToday: current.isSame(today, 'day'),
            });
            current = current.add(1, 'day');
        }
        return result;
    }, [startDate, endDate]);

    // Calculate minimum price across all venues for each day
    const minPricesPerDay = useMemo(() => {
        return days.map((_, index) => {
            const prices = coworkingSpaces
                ?.map(space => space.item_price?.[index])
                .filter(price => price !== null && price !== undefined && typeof price === 'number');
            
            if (prices && prices.length > 0) {
                return Math.min(...prices);
            }
            return null;
        });
    }, [days, coworkingSpaces]);

    const handleDayClick = (index, isPast) => {
        if (isPast) return;
        
        // Toggle: if same day clicked, deselect
        if (selectedDayIndex === index) {
            onDaySelect(null);
        } else {
            onDaySelect(index);
        }
    };

    // Get readable values from the selections
    const getReadableService = (serviceValue) => {
        const service = services.find(s => s.value === serviceValue);
        return service?.label?.toLowerCase() || serviceValue;
    };

    const getReadableArea = (areaValue) => {
        const area = areas.find(a => a.value === areaValue);
        return area?.label || areaValue;
    };

    const MobileFiltersPreview = () => (
        <Box sx={{ textAlign: 'center' }}>
            <Typography variant="body1" sx={{ mb: 1, px: 2, fontSize: '1.1rem'}}>
                Showing <Typography component="span" sx={{ color: 'primary.main', fontWeight: 'bold', fontSize: '1.1rem' }}>{getReadableService(selectedService)}s</Typography> in{' '}
                <Typography component="span" sx={{ color: 'primary.main', fontWeight: 'bold', fontSize: '1.1rem' }}>{getReadableArea(selectedArea)}</Typography>
            </Typography>

            <Button
                variant="contained"
                onClick={() => setShowFilters(true)}
                sx={{
                    borderRadius: '20px',
                    textTransform: 'none',
                    px: 3
                }}
            >
                Search all spaces
            </Button>
        </Box>
    );

    return (
        <Box
            sx={{
                background: isMobile ?
                    'none' :
                    `linear-gradient(rgba(0, 0, 0, 0.4), rgba(0, 0, 0, 0.4)), url(${backgroundImage})`,
                backgroundSize: 'cover',
                backgroundPosition: 'center',
                py: isMobile ? 1 : 2,
                px: isMobile ? 1 : 2,
            }}
        >
            {/* Search Box */}
            <Box
                sx={{
                    backgroundColor: isMobile ? 'transparent' : 'rgba(255, 255, 255, 0.97)',
                    borderRadius: isMobile ? 0 : 3,
                    py: isMobile ? 1 : 2.5,
                    px: isMobile ? 1 : 4,
                    maxWidth: '900px',
                    mx: 'auto',
                    boxShadow: isMobile ? 'none' : '0 8px 32px rgba(0, 0, 0, 0.12)',
                    backdropFilter: isMobile ? 'none' : 'blur(8px)'
                }}
            >
                {/* Title Section */}
                <Typography
                    variant="h6"
                    align="center"
                    sx={{
                        mb: 2,
                        fontWeight: 600,
                        color: theme => theme.palette.primary.main,
                        fontSize: { xs: '1.1rem', sm: '1.25rem' }
                    }}
                >
                    {isLoggedIn ? 'Find your next workspace' : 'Find the best price of the week'}
                </Typography>

                {showFilters ? (
                    <>
                        {/* Row 1: Area and Service Type */}
                        <Box
                            sx={{
                                display: 'flex',
                                gap: 2,
                                alignItems: 'center',
                                justifyContent: 'center',
                                flexWrap: 'wrap',
                                mb: 2,
                            }}
                        >
                            <FormControl
                                sx={{ minWidth: { xs: 140, sm: 180 } }}
                                size="small"
                            >
                                <InputLabel>Area</InputLabel>
                                <Select
                                    value={selectedArea}
                                    label="Area"
                                    onChange={(e) => setSelectedArea(e.target.value)}
                                >
                                    {areas.map((area) => (
                                        <MenuItem
                                            key={area.value}
                                            value={area.value}
                                            disabled={area.disabled}
                                        >
                                            {area.label}
                                            {area.disabled && (
                                                <Typography
                                                    component="span"
                                                    sx={{ ml: 1, fontSize: '0.75rem', color: 'text.disabled' }}
                                                >
                                                    (Coming soon)
                                                </Typography>
                                            )}
                                        </MenuItem>
                                    ))}
                                </Select>
                            </FormControl>

                            <FormControl
                                sx={{ minWidth: { xs: 140, sm: 180 } }}
                                size="small"
                            >
                                <InputLabel>Service Type</InputLabel>
                                <Select
                                    value={selectedService}
                                    label="Service Type"
                                    onChange={(e) => setSelectedService(e.target.value)}
                                >
                                    {services.map((service) => (
                                        <MenuItem
                                            key={service.value}
                                            value={service.value}
                                            disabled={service.disabled}
                                        >
                                            {service.label}
                                            {service.disabled && (
                                                <Typography
                                                    component="span"
                                                    sx={{ ml: 1, fontSize: '0.75rem', color: 'text.disabled' }}
                                                >
                                                    (Coming soon)
                                                </Typography>
                                            )}
                                        </MenuItem>
                                    ))}
                                </Select>
                            </FormControl>

                            <FormControl
                                sx={{ minWidth: { xs: 140, sm: 160 } }}
                                size="small"
                            >
                                <InputLabel>Sort by</InputLabel>
                                <Select
                                    value={sortBy}
                                    label="Sort by"
                                    onChange={(e) => setSortBy(e.target.value)}
                                >
                                    {sortOptions.map((option) => (
                                        <MenuItem
                                            key={option.value}
                                            value={option.value}
                                        >
                                            {option.label}
                                        </MenuItem>
                                    ))}
                                </Select>
                            </FormControl>

                            {/* Price display mode selector */}
                            <FormControl
                                sx={{ minWidth: { xs: 140, sm: 160 } }}
                                size="small"
                            >
                                <InputLabel>Price view</InputLabel>
                                <Select
                                    value={priceDisplayMode}
                                    label="Price view"
                                    onChange={(e) => setPriceDisplayMode(e.target.value)}
                                >
                                    <MenuItem value="week">Mon-Fri prices</MenuItem>
                                    <MenuItem value="best">Best price</MenuItem>
                                </Select>
                            </FormControl>
                        </Box>

                        {/* Row 2: Week Price Selector with arrows */}
                        <Box
                            sx={{
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                gap: 1,
                            }}
                        >
                            {/* Left Arrow */}
                            <IconButton
                                onClick={handlePrevWeek}
                                sx={{
                                    backgroundColor: 'rgba(0,0,0,0.04)',
                                    '&:hover': { backgroundColor: 'rgba(0,0,0,0.08)' },
                                }}
                            >
                                <ChevronLeftIcon />
                            </IconButton>

                            {/* Day Price Buttons */}
                            <Box
                                sx={{
                                    display: 'flex',
                                    gap: { xs: 0.5, sm: 0.75 },
                                    overflowX: 'auto',
                                    scrollbarWidth: 'none',
                                    '&::-webkit-scrollbar': { display: 'none' },
                                    py: 0.25,
                                }}
                            >
                                {days.map((day, index) => (
                                    <Box
                                        key={index}
                                        onClick={() => handleDayClick(index, day.isPast)}
                                        sx={{
                                            display: 'flex',
                                            flexDirection: 'column',
                                            alignItems: 'center',
                                            padding: { xs: '6px 10px', sm: '8px 12px' },
                                            minWidth: { xs: '52px', sm: '62px' },
                                            borderRadius: '10px',
                                            border: '2px solid',
                                            borderColor: selectedDayIndex === index 
                                                ? 'primary.main' 
                                                : 'transparent',
                                            backgroundColor: selectedDayIndex === index 
                                                ? 'rgba(255, 159, 28, 0.1)' 
                                                : 'rgba(0,0,0,0.03)',
                                            opacity: day.isPast ? 0.4 : 1,
                                            cursor: day.isPast ? 'not-allowed' : 'pointer',
                                            transition: 'all 0.2s ease',
                                            '&:hover': {
                                                backgroundColor: day.isPast 
                                                    ? 'rgba(0,0,0,0.03)' 
                                                    : selectedDayIndex === index 
                                                        ? 'rgba(255, 159, 28, 0.15)' 
                                                        : 'rgba(0,0,0,0.06)',
                                                transform: day.isPast ? 'none' : 'translateY(-1px)',
                                            },
                                        }}
                                    >
                                        <Typography
                                            sx={{
                                                fontSize: { xs: '0.65rem', sm: '0.7rem' },
                                                fontWeight: 600,
                                                color: selectedDayIndex === index ? 'primary.main' : '#525252',
                                                textTransform: 'uppercase',
                                                letterSpacing: '0.3px',
                                                lineHeight: 1.2,
                                            }}
                                        >
                                            {day.isToday ? 'Today' : day.dayName}
                                        </Typography>
                                        <Typography
                                            sx={{
                                                fontSize: { xs: '0.55rem', sm: '0.6rem' },
                                                color: '#737373',
                                                mb: 0.25,
                                                lineHeight: 1.2,
                                            }}
                                        >
                                            {day.dayDate} {day.monthName}
                                        </Typography>
                                        <Typography
                                            sx={{
                                                fontSize: { xs: '0.8rem', sm: '0.9rem' },
                                                fontWeight: 700,
                                                color: selectedDayIndex === index ? 'primary.main' : '#171717',
                                                lineHeight: 1.2,
                                            }}
                                        >
                                            {minPricesPerDay[index] !== null 
                                                ? `£${minPricesPerDay[index].toFixed(0)}` 
                                                : '—'}
                                        </Typography>
                                    </Box>
                                ))}
                            </Box>

                            {/* Right Arrow */}
                            <IconButton
                                onClick={handleNextWeek}
                                sx={{
                                    backgroundColor: 'rgba(0,0,0,0.04)',
                                    '&:hover': { backgroundColor: 'rgba(0,0,0,0.08)' },
                                }}
                            >
                                <ChevronRightIcon />
                            </IconButton>
                        </Box>

                        {/* Clear selection hint */}
                        {selectedDayIndex !== null && (
                            <Typography
                                onClick={() => onDaySelect(null)}
                                sx={{
                                    textAlign: 'center',
                                    mt: 1.5,
                                    fontSize: '0.8rem',
                                    color: 'primary.main',
                                    cursor: 'pointer',
                                    '&:hover': { textDecoration: 'underline' },
                                }}
                            >
                                Show all days →
                            </Typography>
                        )}
                    </>
                ) : (
                    <MobileFiltersPreview />
                )}
            </Box>
        </Box>
    );
};

export default SearchBanner;
